import type { ReactNode } from 'react'
import type { SubmissionEntry } from '../../types/jobTracking'

interface SubmissionHistoryListProps {
  submissions: SubmissionEntry[]
  isLoading?: boolean
  maxItems?: number
  onClickSubmission?: (submission: SubmissionEntry) => void
  renderMetric?: (submission: SubmissionEntry) => ReactNode
}

export default function SubmissionHistoryList({
  submissions,
  isLoading,
  maxItems = 10,
  onClickSubmission,
  renderMetric,
}: SubmissionHistoryListProps) {
  const displayed = submissions.slice(0, maxItems)

  return (
    <div className="p-3 border-b border-qcloud-border">
      <h4 className="text-xs font-semibold text-qcloud-text mb-2 uppercase tracking-wider">
        Submissions {submissions.length > 0 && <span className="text-qcloud-muted font-normal">({submissions.length})</span>}
      </h4>
      {displayed.length > 0 ? (
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {displayed.map(sub => (
            <div
              key={sub.id}
              onClick={() => onClickSubmission?.(sub)}
              className={`flex items-center justify-between text-[10px] py-1.5 px-2 rounded ${
                onClickSubmission ? 'cursor-pointer hover:bg-slate-50' : ''
              } ${sub.status === 'failed' ? 'bg-red-50/50' : sub.status === 'completed' ? 'bg-green-50/50' : 'bg-slate-50/50'}`}
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  sub.status === 'completed' ? 'bg-green-500' :
                  sub.status === 'failed' ? 'bg-red-500' :
                  sub.status === 'running' ? 'bg-blue-500 animate-pulse' :
                  'bg-amber-500'
                }`} />
                <span className="font-mono truncate">{sub.id.slice(0, 8)}</span>
                {sub.backend && <span className="text-qcloud-muted">{sub.backend}</span>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                {renderMetric?.(sub)}
                <span className={`font-medium ${
                  sub.status === 'completed' ? 'text-green-600' :
                  sub.status === 'failed' ? 'text-red-500' :
                  sub.status === 'running' ? 'text-blue-600' :
                  'text-amber-600'
                }`}>{sub.status}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-[10px] text-qcloud-muted">
          {isLoading ? 'Loading...' : 'No submissions yet'}
        </div>
      )}
    </div>
  )
}
